import React from 'react';
import styled from '@emotion/styled';
import OrderStatusBar from './OrderStatusBar';
import { MINUTE_IN_MS } from '../lib/time';
import {
  Order,
  computeMaxOrderDuration,
  computeOrderDuration,
  getOrderDate,
} from '../models/order';
import moment from 'moment-timezone';

export interface OrdersTableProps {
  orders: Order[];
  onSelectOrder?: (order: Order) => any;
  highlightedOrder?: Order;
}

const COLUMN_LABELS = [
  'Order',
  'Brand',
  'Status',
  'Time',
  'Date',
  'Start',
];

export const ORDERS_TABLE_CLASS_NAME = 'orders-table';
export const HIGHLIGHTED_CLASS_NAME = 'highlighted';

export default ({
  orders,
  highlightedOrder,
  onSelectOrder = () => { },
}: OrdersTableProps) => {
  const maxDuration = computeMaxOrderDuration(orders);

  return (
    <StyledContainer className={ORDERS_TABLE_CLASS_NAME}>
      <thead>
        <tr>
          {COLUMN_LABELS.map((headerText) => (
            <th key={headerText}>{headerText}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {orders.map((order) => {
          return (
            <OrderRow
              key={order.id}
              order={order}
              maxDuration={maxDuration}
              highlighted={order.id === highlightedOrder?.id}
              onClick={() => onSelectOrder(order)}
            />
          );
        })}
      </tbody>
    </StyledContainer>
  );
};

export const OrderRow = (props: {
  order: Order;
  maxDuration: number;
  highlighted: boolean;
  onClick?: () => any;
}) => {
  const { order, maxDuration, highlighted, onClick = () => { } } = props;
  const orderDate = getOrderDate(order);
  const now = moment();

  let formattedDate;
  if (orderDate.isBefore(now.subtract(6, 'months'))) {
    formattedDate = orderDate.format('ddd, YYYY-MM-DD');
  } else {
    formattedDate = orderDate.format('ddd, MMM D');
  }

  // format just the time
  let orderTime = orderDate.format('hh:mm A');

  return (
    <tr
      onClick={() => onClick()}
      className={highlighted ? HIGHLIGHTED_CLASS_NAME : ''}
    >
      <td className="id">{order.platformId}</td>
      <td>{order.brand.name}</td>
      <td width="100%">
        <OrderStatusBar order={order} maxDuration={maxDuration} />
      </td>
      <td>
        <span role="img" aria-label="">
          🕑
        </span>{' '}
        {Math.floor(computeOrderDuration(order) / MINUTE_IN_MS)} min
      </td>
      <td>{formattedDate}</td>
      <td>{orderTime}</td>
    </tr>
  );
};

export const StyledContainer = styled.table`
  text-align: left;
  font-family: Helvetica, sans-serif;
  font-size: 16px;
  border-collapse: collapse;
  cursor: pointer;

  th,
  td {
    padding: 0.75em 0 0.75em 50px;
    white-space: nowrap;

    &:last-child {
      padding-right: 35px;
    }
  }

  th {
    text-transform: uppercase;
    font-weight: bold;
  }

  td {
    border-top: 1px solid rgba(110, 117, 124, 0.2);
    color: #6e757c;
  }

  tbody {
    tr {
      transition: background 0.2s linear;

      &:hover,
      &.${HIGHLIGHTED_CLASS_NAME} {
        background: #ffd;
      }
    }
  }

  .id {
    color: #325882;
    font-weight: bold;
  }
`;
